import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, CalendarDays, Youtube, Gavel, Users, Folder, Trash2, ExternalLink, Video, CalendarPlus, Pencil, Sparkles } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { googleCalendarUrl, downloadICS } from "@/lib/calendarExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CaseCombobox } from "@/components/CaseCombobox";
import { caseDisplayName } from "@/lib/caseName";

const TYPE_META = {
  case: { label: "Case Date", color: "#F5C518", icon: Folder },
  court: { label: "Court Date", color: "#5B8DEF", icon: Gavel },
  premiere: { label: "Video Premiere", color: "#FF4D4D", icon: Youtube },
  community: { label: "Community", color: "#34C759", icon: Users },
  meeting: { label: "Bullpen Meeting", color: "#F5C518", icon: Video },
  special: { label: "Special Event", color: "#FF8C42", icon: Sparkles },
};
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Calendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [selected, setSelected] = useState(todayStr());
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [cases, setCases] = useState([]);
  const [form, setForm] = useState({ title: "", date: todayStr(), time: "", type: "community", description: "", link: "", case_id: "", image: "" });

  const load = () => api.get("/calendar/events").then(({ data }) => setEvents(data)).catch(() => {});
  useEffect(() => { load(); api.get("/cases").then(({ data }) => setCases(data)).catch(() => {}); }, []);

  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [events]);

  const grid = useMemo(() => {
    const first = new Date(cursor.y, cursor.m, 1);
    const start = first.getDay();
    const days = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < start; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(`${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    return cells;
  }, [cursor]);

  const move = (delta) => {
    let m = cursor.m + delta, y = cursor.y;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    setCursor({ y, m });
  };

  const upcoming = useMemo(() => events.filter((e) => e.date >= todayStr()).slice(0, 12), [events]);

  const jumpTo = (date) => {
    const d = new Date(date + "T00:00:00");
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
    setSelected(date);
  };

  const resetForm = () => setForm({ title: "", date: todayStr(), time: "", type: "community", description: "", link: "", case_id: "", image: "" });
  const openAdd = () => { setEditingId(null); resetForm(); setOpen(true); };
  const startEdit = (e) => {
    setEditingId(e.id);
    setForm({ title: e.title || "", date: e.date || todayStr(), time: e.time || "", type: e.type || "community", description: e.description || "", link: e.link || "", case_id: e.case_id || "", image: e.image || "" });
    setOpen(true);
  };
  const uploadImage = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/calendar/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploadingImg(false); }
  };
  const saveEvent = async () => {
    if (!form.title || !form.date) { toast.error("Title and date required"); return; }
    try {
      if (editingId) {
        await api.put(`/calendar/events/${editingId}`, { ...form, case_id: form.case_id || "" });
        toast.success("Event updated");
      } else {
        await api.post("/calendar/events", { ...form, case_id: form.case_id || "" });
        toast.success("Event added");
      }
      setOpen(false); setEditingId(null); resetForm();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (e) => {
    const extra = e.source === "timeline" ? `\n\nThis also removes it from the ${e.case_title || "case"} timeline.` : "";
    if (!window.confirm(`Remove this date?\n\n${e.date} — ${e.title}${extra}`)) return;
    try { await api.delete(`/calendar/events/${e.id}`); load(); toast.success("Removed"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const selectedEvents = byDate[selected] || [];

  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1"><CalendarDays className="w-4 h-4 text-gold" /><span className="label-mono">Case Calendar</span></div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Dates, premieres & events.</h1>
          </div>
          {isAdmin && (
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); resetForm(); } }}>
              <DialogTrigger asChild><Button data-testid="add-calendar-event-btn" onClick={openAdd} className="bg-gold text-charcoal hover:bg-gold-hover rounded-sm"><Plus className="w-4 h-4 mr-1" /> Add Event</Button></DialogTrigger>
              <DialogContent className="bg-surface border-white/10 text-white">
                <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit Calendar Event" : "New Calendar Event"}</DialogTitle></DialogHeader>
                <Input placeholder="Event title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="bg-charcoal border-white/15" data-testid="cal-title-input" />
                <div className="grid grid-cols-2 gap-3">
                  <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="bg-charcoal border-white/15" data-testid="cal-date-input" />
                  <Input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className="bg-charcoal border-white/15" />
                </div>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger className="bg-charcoal border-white/15"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">
                    <SelectItem value="court">Court Date</SelectItem>
                    <SelectItem value="premiere">Video Premiere</SelectItem>
                    <SelectItem value="community">Community Event</SelectItem>
                    <SelectItem value="meeting">Bullpen Meeting</SelectItem>
                    <SelectItem value="special">Special Event</SelectItem>
                    <SelectItem value="case">Case Date</SelectItem>
                  </SelectContent>
                </Select>
                <CaseCombobox
                  cases={cases}
                  value={form.case_id || ""}
                  onChange={(id) => setForm((f) => ({ ...f, case_id: id }))}
                />
                <Input placeholder="Link (e.g. YouTube URL, optional)" value={form.link} onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))} className="bg-charcoal border-white/15" />
                <div className="rounded-sm border border-white/15 bg-charcoal p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-white/60">Event image (optional)</span>
                    <label className="cursor-pointer text-xs font-semibold text-gold hover:text-gold-hover" data-testid="cal-image-upload-label">
                      {uploadingImg ? "Uploading…" : (form.image ? "Replace" : "Upload image")}
                      <input type="file" accept="image/*" className="hidden" disabled={uploadingImg}
                        onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="cal-image-input" />
                    </label>
                  </div>
                  {form.image && (
                    <div className="mt-3 relative">
                      <img src={form.image} alt="event" className="w-full h-32 object-cover rounded-sm" />
                      <button type="button" onClick={() => setForm((f) => ({ ...f, image: "" }))}
                        className="absolute top-2 right-2 bg-charcoal/90 border border-white/15 rounded-sm p-1.5 text-white/70 hover:text-red-400" data-testid="cal-image-remove">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  <p className="text-[11px] text-white/35 mt-2">Falls back to the case or Bullpen image if left empty.</p>
                </div>
                <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="bg-charcoal border-white/15" />
                <DialogFooter><Button onClick={saveEvent} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="save-calendar-event-btn">{editingId ? "Save Changes" : "Add Event"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          {Object.entries(TYPE_META).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5 text-xs text-white/50 font-mono">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} /> {v.label}
            </span>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Month grid */}
          <div className="lg:col-span-2 card-surface rounded-md p-5">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-semibold text-white">{MONTHS[cursor.m]} {cursor.y}</h2>
              <div className="flex gap-1">
                <button onClick={() => move(-1)} data-testid="cal-prev-btn" className="p-2 rounded-sm border border-white/10 text-white/60 hover:border-gold/40 hover:text-gold transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                <button onClick={() => move(1)} data-testid="cal-next-btn" className="p-2 rounded-sm border border-white/10 text-white/60 hover:border-gold/40 hover:text-gold transition-colors"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {DOW.map((d) => <div key={d} className="text-center label-mono py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {grid.map((date, i) => {
                if (!date) return <div key={`empty-${i}`} />;
                const evs = byDate[date] || [];
                const isToday = date === todayStr();
                const isSel = date === selected;
                return (
                  <button key={date} onClick={() => setSelected(date)} data-testid={`cal-day-${date}`}
                    className={`min-h-[64px] sm:min-h-[88px] rounded-sm border p-1.5 flex flex-col items-stretch text-left gap-0.5 overflow-hidden transition-colors ${isSel ? "border-gold bg-gold/10" : "border-white/5 hover:border-white/20"}`}>
                    <span className={`text-xs font-mono ${isToday ? "text-gold font-bold" : "text-white/60"}`}>{parseInt(date.slice(-2))}</span>
                    {evs.slice(0, 2).map((e) => {
                      const color = TYPE_META[e.type]?.color || "#888";
                      return (
                        <span key={e.id} title={e.title}
                          className="w-full truncate text-[10px] leading-tight px-1 py-0.5 rounded-sm font-medium"
                          style={{ background: `${color}22`, color, borderLeft: `2px solid ${color}` }}>
                          {e.title}
                        </span>
                      );
                    })}
                    {evs.length > 2 && <span className="text-[9px] text-white/40 font-mono px-1">+{evs.length - 2} more</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected day + upcoming */}
          <div className="space-y-6">
            <div className="card-surface rounded-md p-5">
              <div className="label-mono mb-3">{new Date(selected + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
              {selectedEvents.length === 0 ? <p className="text-sm text-white/40">No events on this day.</p> : (
                <div className="space-y-3">{selectedEvents.map((e) => <EventRow key={e.id} e={e} isAdmin={isAdmin} onDelete={del} onEdit={startEdit} />)}</div>
              )}
            </div>
            <div className="card-surface rounded-md p-5">
              <div className="label-mono mb-3">Upcoming</div>
              {upcoming.length === 0 ? <p className="text-sm text-white/40">Nothing scheduled.</p> : (
                <div className="space-y-3">{upcoming.map((e) => <EventRow key={e.id + e.source} e={e} isAdmin={isAdmin} onDelete={del} onEdit={startEdit} showDate onJump={jumpTo} />)}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function EventRow({ e, isAdmin, onDelete, onEdit, showDate, onJump }) {
  const meta = TYPE_META[e.type] || TYPE_META.community;
  const Icon = meta.icon;
  return (
    <div
      onClick={onJump ? () => onJump(e.date) : undefined}
      className={`flex items-start gap-3 border-b border-white/5 pb-3 last:border-0 last:pb-0 ${onJump ? "cursor-pointer hover:bg-white/5 -mx-2 px-2 rounded-sm transition-colors" : ""}`}
      data-testid={`cal-event-${e.id}`}
    >
      <div className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${meta.color}1A`, border: `1px solid ${meta.color}40` }}>
        <Icon className="w-4 h-4" style={{ color: meta.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-white leading-tight">{e.title}</h4>
        <div className="label-mono mt-0.5">
          {showDate && (<>{new Date(e.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}{e.time && " · "}</>)}
          {e.time}
        </div>
        {e.description && <p className="text-xs text-white/50 mt-1 leading-relaxed">{e.description}</p>}
        <div className="flex items-center gap-3 mt-1.5">
          {e.case_slug && <Link to={`/cases/${e.case_slug}`} onClick={(ev) => ev.stopPropagation()} className="text-xs text-gold hover:underline">{caseDisplayName(e.case_title)}</Link>}
          {e.link && <a href={e.link} target="_blank" rel="noreferrer" onClick={(ev) => ev.stopPropagation()} className="text-xs text-white/50 hover:text-gold inline-flex items-center gap-1">Link <ExternalLink className="w-3 h-3" /></a>}
          <AddToCalendar e={e} />
        </div>
      </div>
      {isAdmin && (
        <div className="flex items-center gap-1 shrink-0">
          {e.source === "calendar" && (
            <button onClick={(ev) => { ev.stopPropagation(); onEdit(e); }} className="text-white/30 hover:text-gold transition-colors" data-testid={`cal-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></button>
          )}
          <button onClick={(ev) => { ev.stopPropagation(); onDelete(e); }} className="text-white/30 hover:text-red-400 transition-colors" data-testid={`cal-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
}

function AddToCalendar({ e }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);
  return (
    <span className="relative">
      <button
        data-testid={`cal-add-to-calendar-${e.id}`}
        onClick={(ev) => { ev.stopPropagation(); setOpen((o) => !o); }}
        className="text-xs text-white/50 hover:text-gold inline-flex items-center gap-1"
      >
        <CalendarPlus className="w-3 h-3" /> Add to calendar
      </button>
      {open && (
        <div
          onClick={(ev) => ev.stopPropagation()}
          className="absolute z-20 mt-1 left-0 w-44 rounded-sm border border-white/10 bg-surface shadow-xl py-1"
          data-testid={`cal-add-menu-${e.id}`}
        >
          <a
            href={googleCalendarUrl(e)}
            target="_blank" rel="noreferrer"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-xs text-white/80 hover:bg-white/5 hover:text-gold"
          >
            Google Calendar
          </a>
          <button
            onClick={() => { downloadICS(e); setOpen(false); }}
            className="block w-full text-left px-3 py-2 text-xs text-white/80 hover:bg-white/5 hover:text-gold"
          >
            Apple / Outlook (.ics)
          </button>
        </div>
      )}
    </span>
  );
}
